import { useCallback, useMemo } from 'react'
import useQueryParameters, { queryParameters } from './useQueryParameters'

const searchableListQueryParameters = queryParameters().number('page').string('search')

interface SearchableListInit<T> {
  prefix: string
  pageSize: number
  filter: (search: string) => (item: T) => boolean
}

export default function useSearchableList<T>({ prefix, pageSize, filter }: SearchableListInit<T>, allItems: T[]) {
  const queryParameters = useMemo(() => searchableListQueryParameters.prefixed(prefix), [prefix])

  const { page, search, set } = useQueryParameters(queryParameters)
  const setPage = useCallback((page: number) => set({ page }, { preventScrollReset: true }), [set])
  const setSearch = useCallback((search: string) => set({ search, page: 0 }, { preventScrollReset: true }), [set])

  const filteredItems = useMemo(() => (search ? allItems.filter(filter(search)) : allItems), [allItems, search, filter])

  const pageCount = Math.ceil(filteredItems.length / pageSize)

  const items = useMemo(() => filteredItems.slice(page * pageSize, (page + 1) * pageSize), [filteredItems, page])

  return {
    page,
    setPage,
    search,
    setSearch,
    items,
    pageSize,
    pageCount,
    itemCount: items.length,
  }
}
